import { AUFTRAG_API } from '@Constants/backendConstants';
import { Permission } from '@Constants/Permission.g';
import { useHasPermission } from '@Contexts/appContext';
import { useIsPortalSession } from '@Contexts/session';
import { type ApiOrder, type EnumContainerActionRequest } from '@Model';
import { isOk, type Result, useOnTheFlyModal, useSynqoHttp, useToasts } from '@synqo/frontend-component-library';
import { requisitionWizardPath } from '@utils/requisitionWizardPath';
import { useCallback, useState } from 'react';
import { useClientNavigate } from '../../../../Routing/useClientNavigate';
import { type ContainerPlanSource, FrameEditOrder, toApiSource } from '../Editing';
import { type FollowUpDraftView, type PrefillFollowUpCommand } from './containerFollowUpCommands';

/** What a new draft can be created from: a copy of a row of another draft makes no sense. */
export type NewDraftPlanSource = Exclude<ContainerPlanSource, { draftContainerActionId: string }>;

export interface NewDraftPlanningOptions {
    /** Called once the prepared order has been saved. */
    onSaved?: () => void;
}

export interface NewDraftPlanning {
    /** Whether the session may create the draft: an order in the ERP, a requisition in the portal. */
    canPlan: boolean;
    /** Whether a draft is being prepared. */
    isPlanning: boolean;
    /**
     * Has the server prepare one draft from the source and opens it: the order in a modal in the ERP, the wizard on
     * the requisition in the portal. Resolves to whether the draft was opened.
     */
    plan: (
        action: EnumContainerActionRequest,
        source: NewDraftPlanSource,
        replacementContainerTypeId?: string,
    ) => Promise<boolean>;
}

/**
 * Prepares a new draft from a source: an order in the ERP, whose modal is closed without saving to discard it, and a
 * requisition in the portal.
 */
// @ai-generated 2026-10-09T21:45:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function useContainerPlanningOnNewDraft({ onSaved }: NewDraftPlanningOptions = {}): NewDraftPlanning {
    const isPortalSession = useIsPortalSession();
    const hasPermission = useHasPermission();
    const http = useSynqoHttp(AUFTRAG_API);
    const { pushAllToasts, pushToast, remove } = useToasts();
    const navigate = useClientNavigate();
    const { openOnTheFlyModal, closeOnTheFlyModals } = useOnTheFlyModal();
    const [isPlanning, setIsPlanning] = useState(false);

    const canPlan = hasPermission(
        isPortalSession ? Permission.AuftragsbearbeitungRequisitionsCreate : Permission.AuftragsbearbeitungOrdersCreate,
    );

    const announceCreated = useCallback(
        (order: ApiOrder) => {
            const toastId = pushToast({
                message: `Auftrag ${order.number} wurde angelegt.`,
                variant: 'success',
                actions: [
                    {
                        id: 'open-order',
                        label: 'Auftrag öffnen',
                        variant: 'success',
                        onClick: () => {
                            remove(toastId);
                            navigate(`/orders/${order.id}`);
                        },
                    },
                ],
            });
        },
        [pushToast, remove, navigate],
    );

    const openOrder = useCallback(
        (draftId: string) => {
            let modalId = '';
            const close = () => closeOnTheFlyModals(modalId);

            modalId = openOnTheFlyModal({
                child: (
                    <FrameEditOrder
                        primaryKey={draftId}
                        embedded
                        canEdit
                        canCreate
                        canDelete={false}
                        events={{
                            onBack: undefined,
                            onClose: close,
                            onSave: (order) => {
                                close();
                                if (order !== undefined) announceCreated(order);
                                onSaved?.();
                                return true;
                            },
                        }}
                    />
                ),
            });
        },
        [openOnTheFlyModal, closeOnTheFlyModals, announceCreated, onSaved],
    );

    const plan = useCallback(
        async (
            action: EnumContainerActionRequest,
            source: NewDraftPlanSource,
            replacementContainerTypeId?: string,
        ): Promise<boolean> => {
            setIsPlanning(true);
            try {
                const result = await http.post<Result<FollowUpDraftView>, PrefillFollowUpCommand>(
                    isPortalSession ? '/requisitions:prefill_follow_up.v1' : '/orders:prefill_follow_up.v1',
                    {
                        follow_up: action,
                        source: toApiSource(source),
                        replacement_container_type_id: replacementContainerTypeId,
                    },
                );

                pushAllToasts(result.messages);

                if (!isOk(result)) {
                    return false;
                }

                if (isPortalSession) {
                    navigate(requisitionWizardPath(result.data.id));
                } else {
                    openOrder(result.data.id);
                }

                return true;
            } finally {
                setIsPlanning(false);
            }
        },
        [http, isPortalSession, pushAllToasts, navigate, openOrder],
    );

    return { canPlan, isPlanning, plan };
}
